import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarIcon, CheckCircle, XCircle, Clock, Users, Plus, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { employeeAPI, attendanceAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const Attendance = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const [date, setDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
  const [isEditEmployeeOpen, setIsEditEmployeeOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    designation: '',
    phone: '',
    email: '',
    status: 'Active'
  });

  useEffect(() => {
    loadData();
  }, [date]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [employeesData, attendanceData] = await Promise.all([
        employeeAPI.getAll(),
        attendanceAPI.getAll(format(date, 'yyyy-MM-dd'))
      ]);
      setEmployees(employeesData);
      setAttendanceRecords(attendanceData);
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAttendanceForEmployee = (employeeId) => {
    return attendanceRecords.find(a => a.employee_id === employeeId);
  };

  const handleMarkAttendance = async (employee, status) => {
    const selectedDate = format(date, 'yyyy-MM-dd');
    const existingAttendance = getAttendanceForEmployee(employee.id);
    
    try {
      if (existingAttendance) {
        // Update existing attendance
        const updated = await attendanceAPI.update(existingAttendance.id, { status });
        setAttendanceRecords(attendanceRecords.map(a => 
          a.id === existingAttendance.id ? updated : a
        ));
      } else {
        // Create new attendance
        const newAttendance = await attendanceAPI.create({
          employee_id: employee.id,
          employee_name: employee.name,
          date: selectedDate,
          status,
          check_in: status === 'Present' ? format(new Date(), 'HH:mm') : null
        });
        setAttendanceRecords([...attendanceRecords, newAttendance]);
      }
      toast.success(`Attendance marked as ${status}`);
    } catch (error) {
      toast.error('Failed to mark attendance');
      console.error('Error marking attendance:', error);
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.name.trim() || !newEmployee.designation.trim()) {
      toast.error('Please fill in name and designation');
      return;
    }

    try {
      const created = await employeeAPI.create(newEmployee);
      setEmployees([...employees, created]);
      setNewEmployee({
        name: '',
        designation: '',
        phone: '',
        email: '',
        status: 'Active'
      });
      setIsAddEmployeeOpen(false);
      toast.success('Employee added successfully');
    } catch (error) {
      toast.error('Failed to add employee');
      console.error('Error adding employee:', error);
    }
  };

  const handleEditEmployee = async () => {
    if (!editingEmployee.name.trim() || !editingEmployee.designation.trim()) {
      toast.error('Please fill in name and designation');
      return;
    }

    try {
      const updated = await employeeAPI.update(editingEmployee.id, {
        name: editingEmployee.name,
        designation: editingEmployee.designation,
        phone: editingEmployee.phone,
        email: editingEmployee.email,
        status: editingEmployee.status
      });
      setEmployees(employees.map(e => e.id === editingEmployee.id ? updated : e));
      setIsEditEmployeeOpen(false);
      setEditingEmployee(null);
      toast.success('Employee updated successfully');
    } catch (error) {
      toast.error('Failed to update employee');
      console.error('Error updating employee:', error);
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      await employeeAPI.delete(id);
      setEmployees(employees.filter(e => e.id !== id));
      toast.success('Employee deleted successfully');
    } catch (error) {
      toast.error('Failed to delete employee');
      console.error('Error deleting employee:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Present':
        return <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" />Present</Badge>;
      case 'Absent':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Absent</Badge>;
      case 'Leave':
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Leave</Badge>;
      case 'Half-day':
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Half-day</Badge>;
      default:
        return <Badge variant="outline">Not Marked</Badge>;
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const attendance = getAttendanceForEmployee(emp.id);
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.designation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'present' && attendance?.status === 'Present') ||
                         (filterStatus === 'absent' && (!attendance || attendance.status === 'Absent')) ||
                         (filterStatus === 'leave' && attendance?.status === 'Leave');
    return matchesSearch && matchesStatus && emp.status === 'Active';
  });

  const stats = {
    total: employees.filter(e => e.status === 'Active').length,
    present: attendanceRecords.filter(a => a.status === 'Present' && a.date === format(date, 'yyyy-MM-dd')).length,
    absent: employees.filter(e => e.status === 'Active').length - attendanceRecords.filter(a => a.date === format(date, 'yyyy-MM-dd')).length,
    leave: attendanceRecords.filter(a => a.status === 'Leave' && a.date === format(date, 'yyyy-MM-dd')).length
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10">
          <Users className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Attendance</h1>
          <p className="text-muted-foreground mt-1">Track employee attendance and working hours</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">Total Employees</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{stats.present}</div>
            <p className="text-sm text-muted-foreground">Present Today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{stats.absent}</div>
            <p className="text-sm text-muted-foreground">Absent Today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{stats.leave}</div>
            <p className="text-sm text-muted-foreground">On Leave</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex gap-2 flex-wrap">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(newDate) => newDate && setDate(newDate)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs"
              />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="leave">On Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={isAddEmployeeOpen} onOpenChange={setIsAddEmployeeOpen}>
              {canAdd('attendance') && (
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Employee
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Employee</DialogTitle>
                  <DialogDescription>Enter employee details</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
                  <div className="grid gap-2">
                    <Label>Name *</Label>
                    <Input
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                      placeholder="Employee name"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Designation *</Label>
                    <Input
                      value={newEmployee.designation}
                      onChange={(e) => setNewEmployee({ ...newEmployee, designation: e.target.value })}
                      placeholder="e.g., Goldsmith"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Phone</Label>
                    <Input
                      value={newEmployee.phone}
                      onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddEmployeeOpen(false)}>Cancel</Button>
                  <Button onClick={handleAddEmployee}>Add Employee</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No employees found</p>
              <p className="text-muted-foreground">Add your first employee to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => {
                    const attendance = getAttendanceForEmployee(employee.id);
                    return (
                      <TableRow key={employee.id}>
                        <TableCell className="font-medium">{employee.name}</TableCell>
                        <TableCell>{employee.designation}</TableCell>
                        <TableCell>{getStatusBadge(attendance?.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {!attendance && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMarkAttendance(employee, 'Present')}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Present
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMarkAttendance(employee, 'Absent')}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Absent
                                </Button>
                              </>
                            )}
                            {canEdit('attendance') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingEmployee(employee);
                                  setIsEditEmployeeOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete('attendance') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEmployee(employee.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Employee Dialog */}
      <Dialog open={isEditEmployeeOpen} onOpenChange={setIsEditEmployeeOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee details</DialogDescription>
          </DialogHeader>
          {editingEmployee && (
            <div className="grid gap-4 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              <div className="grid gap-2">
                <Label>Name *</Label>
                <Input
                  value={editingEmployee.name}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, name: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Designation *</Label>
                <Input
                  value={editingEmployee.designation}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, designation: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Phone</Label>
                <Input
                  value={editingEmployee.phone || ''}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, phone: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input
                  value={editingEmployee.email || ''}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, email: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select
                  value={editingEmployee.status}
                  onValueChange={(value) => setEditingEmployee({ ...editingEmployee, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditEmployeeOpen(false)}>Cancel</Button>
            <Button onClick={handleEditEmployee}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;